import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Icon, Sidebar, toast } from '@node-space/ui-components'
import { appBannersId } from 'components/app/AppBanners'
import { DurationInMilliseconds, PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useSwitchAccountMutation } from 'hooks/mutations/useSwitchAccountMutation'
import { useSidebarLinks } from 'hooks/sidebar/useSidebarLinks'
import { useUserMenuItems } from 'hooks/sidebar/useUserMenuItems'
import { useComponentHeight } from 'hooks/useComponentHeight'
import { reloadPage } from 'utils/utils'

interface SidebarProps {
  setSidebarOpen: Dispatch<SetStateAction<boolean>>
}

const SideBar = ({ setSidebarOpen }: SidebarProps) => {
  const location = useLocation()
  const { t } = useTranslation()
  const { links, footerLink } = useSidebarLinks({ setSidebarOpen })
  const userMenuItems = useUserMenuItems()
  const { profile } = useProfileContext()
  const { accounts, currentAccount } = useAccountsContext()
  const { mutate: switchAccount } = useSwitchAccountMutation()
  const navigate = useNavigate()
  const bannerSpacing = useComponentHeight(appBannersId)

  const sidebarSections = useMemo(
    () => [
      {
        title: 'Navigation',
        items:
          links
            ?.filter(link => !link.isHidden)
            .map(link => ({
              title: link.title,
              Icon: link.icon,
              isActive:
                location.pathname === link.to ||
                location.pathname.startsWith((link.to?.toString() ?? '') + '/'),
              badgeCount: link.highlight ? Number(link.highlight) : undefined,
              disabled: link.requiresVerification,
              onClick: () => {
                if (link.sideBarToggleCallback) {
                  link.sideBarToggleCallback(false)
                  navigate(link.to?.toString() ?? '')
                }
              },
            })) ?? [],
      },
      {
        items: [
          {
            to: `${PathNames.SETTINGS}${PathNames.MANAGE_ACCOUNT}`,
            title: t('manageAccount'),
            Icon: <Icon name="Gear" />,
            sideBarToggleCallback: setSidebarOpen,
            onClick: () => navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_ACCOUNT}`),
          },
        ],
      },
    ],
    [links, location.pathname]
  )

  const accountSwitcherProps = useMemo(
    () => ({
      workspaceAccounts: {
        accountName: profile ? `${profile.firstName} ${profile.lastName}` : '',
        accounts:
          accounts?.map(account => ({
            ...account,
            id: account.id.toString(),
          })) ?? [],
        selectedAccount: currentAccount
          ? {
              ...currentAccount,
              id: currentAccount.id.toString(),
            }
          : undefined,
        setSelectedAccount: (index: number) => {
          const accountIdToSwitchTo = accounts?.[index]?.id
          if (accounts?.length && currentAccount?.id !== accountIdToSwitchTo) {
            switchAccount(accountIdToSwitchTo, {
              onSuccess: () => {
                if (
                  location.pathname.includes(PathNames.CUSTOMERS) ||
                  location.pathname.includes(PathNames.WALLETS)
                ) {
                  navigate(PathNames.DASHBOARD)
                }
                reloadPage()
              },
              onError: () => {
                toast({
                  header: `${t('switchAccountError')} - ${accounts?.[index]?.name}`,
                  variant: 'error',
                  duration: DurationInMilliseconds.TWENTY_SECONDS,
                })
              },
            })
          }
        },
      },
      menuListItems: userMenuItems,
    }),
    [profile, accounts, currentAccount, switchAccount, userMenuItems, location.pathname]
  )

  return (
    <Sidebar
      topOffset={bannerSpacing}
      sidebarSections={sidebarSections}
      accountSwitcher={accountSwitcherProps}
      footer={{
        title: footerLink?.title,
        logoUrl: footerLink?.logoUrl,
        url: footerLink?.url,
        onClick: () => {
          if (footerLink?.to) {
            navigate(footerLink?.to)
          }
        },
      }}
    />
  )
}

export default SideBar
